import { differenceInCalendarMonths } from 'date-fns';
import delv from 'dlv';
import { IntlFormatters } from 'react-intl';

import { TipAmounts } from 'state/order';
import { CardType, IPaymentMethod } from 'state/payment/types';
import { ISOs, PaymentISOs } from 'utils/form/constants';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';

import { sanitizeAlphanumeric, sanitizeNumber } from '../form';

import { DEFAULT_BILLING_COUNTRY } from './default-billing-country';
import { getBillingCountryError } from './get-billing-country-error';

interface ICCFormAddress {
  billingStreetAddress: string;
  billingApt: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
}

interface IDeliveryAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

export interface IPaymentErrors {
  nameOnCard: string;
  cardNumber: string;
  cardType: string;
  cvv: string;
  expiry: string;
  billingStreetAddress: string;
  billingApt: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry?: string;
}

export interface IPaymentState {
  nameOnCard: string;
  cardNumber: string;
  isCardNumberValid: boolean;
  isExpiryValid: boolean;
  cardType: string;
  cvv: string;
  expiry: string;
  billingStreetAddress: string;
  billingApt: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: ISOs;
  billingAddressSameAsDelivery: boolean;
  saveCard: boolean;
}

export interface IAdyenPaymentState {
  nameOnCard: string;
  cardNumber: string;
  isCardNumberValid: boolean;
  isExpiryValid: boolean;
  cardType: string;
  cvv: string;
  expiry: string;
  billingStreetAddress: string;
  billingApt: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: ISOs;
  billingAddressSameAsDelivery: boolean;
  saveCard: boolean;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
}

export interface IGiftCardPaymentErrors {
  cardNumber: string;
}

export interface IGiftCardPaymentState {
  cardNumber: string;
  isCardNumberValid: boolean;
  saveCard: boolean;
}

/**
 * "First data" billing address.
 *
 * Note: There is no such thing as a first data billing
 * address. Check the FirstData and payment gateway
 * spec to ensure format is compliant.
 *
 * https://firstdatanp-ucomgateway.apigee.io/apis/ucomaccountservices/index
 */
export type IFirstDataBillingAddress = PaypalBillingAddress | VisaBillingAddress;

/**
 * Billing address format supported by Visa.
 *
 * https://developer.paypal.com/docs/api/reference/country-codes/#
 */
type PaypalBillingAddress = {
  unitNumber: string;
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
  /** IS0-3166-1 country code (2 char only) */
  country: PaymentISOs;
};

/**
 * Billing address format supported by Visa.
 * https://developer.visa.com/capabilities/pav/reference
 **/
type VisaBillingAddress = {
  unitNumber: string;
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
  /** IS0-3166-1 country code */
  country: ISOs;
};

interface ICCExpiry {
  month: string;
  year: string;
}

export interface IFirstDataPaymentPayload {
  billingAddress: IFirstDataBillingAddress;
  cardNumber: string;
  cardType: string;
  expiryDate: ICCExpiry;
  nameOnCard: string;
  securityCode: string;
}

export interface IFraudPreventionValues {
  billingAddress: IFirstDataBillingAddress;
  fullName: string;
  ccBin: string;
  ccLast4: string;
  expiry: string;
}

enum FormFields {
  CARD_NUMBER = 'cardNumber',
  EXPIRY = 'expiry',
  BILLING_ZIP = 'billingZip'
}

const visaRegEx: RegExp = /^4[0-9]{12}(?:[0-9]{3})?$/;
const mastercardRegEx: RegExp = /^5[1-5][0-9]{14}$/;
export const amexpRegEx: RegExp = /^3[47][0-9]{13}$/;
const discoverRegEx: RegExp = /^6(?:011|5[0-9]{2})[0-9]{12}$/;
const jcbRegex: RegExp = /^(?:2131|2100|1800|35\d{3})\d{11}$/;
const dinersclubRegex: RegExp = /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/;

export function validateCreditCardNumber(
  cardNumber: string,
  supportedCardTypes: CardType[]
): { isValid: boolean; cardType?: CardType } {
  let cardType: CardType | undefined;

  if (visaRegEx.test(cardNumber)) {
    cardType = 'VISA';
  } else if (mastercardRegEx.test(cardNumber)) {
    cardType = 'MASTERCARD';
  } else if (amexpRegEx.test(cardNumber)) {
    cardType = 'AMEX';
  } else if (discoverRegEx.test(cardNumber)) {
    cardType = 'DISCOVER';
  } else if (jcbRegex.test(cardNumber.replace(/\s/g, ''))) {
    cardType = 'JCB';
  } else if (dinersclubRegex.test(cardNumber.replace(/\s/g, ''))) {
    cardType = 'DINERS_CLUB';
  }

  return { isValid: Boolean(cardType && supportedCardTypes.includes(cardType)), cardType };
}

export function isGiftCardNumberValid(cardNumber: string): boolean {
  return /^[0-9]{16}$/.test(cardNumber);
}

export function validateExpiry(expiryMonthAndYear: string): boolean {
  const [expiryMonth, expiryYear]: string[] = expiryMonthAndYear.split('/');
  const now: Date = new Date();
  const expiry: Date = new Date(Number(`20${expiryYear}`), Number(expiryMonth) - 1);

  return differenceInCalendarMonths(expiry, now) >= 0;
}

export function excludeNumeric(string: string): string {
  return string.replace(/^\d$/g, '').trim();
}

/** Takes UK post code and returns formatted string (uppercase and with space) */
export const parseUkPostCode = (postCode: string) => {
  // UK Post Code format - https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s16.html
  const regex = /^([A-Z]{1,2}[0-9R][0-9A-Z]?)\s?([0-9][ABD-HJLNP-UW-Z]{2})$/i.exec(postCode);

  // This shouldn't occur - validation should catch this
  if (!regex) {
    throw Error('Bad post code slipped past validation');
  }

  return `${regex[1]} ${regex[2]}`.toUpperCase();
};

export const transformPayment = (paymentValues: IPaymentState): IFirstDataPaymentPayload => {
  const [expiryMonth, expiryYear]: string[] = paymentValues.expiry.split('/');

  return {
    billingAddress: {
      country: ({ [ISOs.GBR]: 'GB', [ISOs.USA]: 'US', [ISOs.CA]: 'CA' } as const)[
        paymentValues.billingCountry
      ],
      postalCode:
        paymentValues.billingCountry === 'GBR'
          ? parseUkPostCode(paymentValues.billingZip)
          : sanitizeAlphanumeric(paymentValues.billingZip),
      unitNumber: paymentValues.billingApt,
      locality: paymentValues.billingCity,
      region: paymentValues.billingState,
      streetAddress: paymentValues.billingStreetAddress
    },
    cardNumber: sanitizeNumber(paymentValues.cardNumber),
    cardType: paymentValues.cardType,
    expiryDate: {
      month: expiryMonth,
      year: expiryYear
    },
    nameOnCard: paymentValues.nameOnCard,
    securityCode: paymentValues.cvv
  };
};

export const transformAdyenPayment = (paymentValues: IAdyenPaymentState) => ({
  billingAddress: {
    country: ({ [ISOs.GBR]: 'GB', [ISOs.USA]: 'US', [ISOs.CA]: 'CA' } as const)[
      paymentValues.billingCountry
    ],
    postalCode:
      paymentValues.billingCountry === 'GBR'
        ? parseUkPostCode(paymentValues.billingZip)
        : sanitizeAlphanumeric(paymentValues.billingZip),
    unitNumber: paymentValues.billingApt,
    locality: paymentValues.billingCity,
    // @TODO: Remove hardcoded default for Adyen when region input is made available on add payment method form (Reliability Platform task)
    region: paymentValues.billingState,
    streetAddress: paymentValues.billingStreetAddress
  },
  cardNumber: paymentValues.cardNumber,
  cardType: paymentValues.cardType,
  expiryDate: {
    month: paymentValues.encryptedExpiryMonth,
    year: paymentValues.encryptedExpiryYear
  },
  nameOnCard: paymentValues.nameOnCard,
  securityCode: paymentValues.cvv
});

export const onGiftCardFormChange = (
  name: string,
  value: string,
  state: IGiftCardPaymentState,
  formErrors: IPaymentErrors
): { state: IGiftCardPaymentState; formErrors: IPaymentErrors } => {
  if (name === FormFields.CARD_NUMBER) {
    const isValid: boolean = isGiftCardNumberValid(sanitizeNumber(value));

    formErrors.cardNumber = '';

    state.cardNumber = sanitizeNumber(value);
    state.isCardNumberValid = isValid;
  }

  return { state, formErrors };
};

export const getGiftCardFormErrors = (
  state: IGiftCardPaymentState,
  formErrors: IGiftCardPaymentErrors,
  formatMessage: IntlFormatters['formatMessage']
): { hasErrors: boolean; formErrors: IGiftCardPaymentErrors } => {
  if (!state.isCardNumberValid) {
    formErrors.cardNumber = formatMessage({ id: 'giftCardNumberIsNotValid' });
  }
  if (!state.cardNumber.trim()) {
    formErrors.cardNumber = formatMessage({ id: 'giftCardNumberIsRequired' });
  }

  const hasErrors: boolean = Object.values(formErrors).some(err => err);

  return { hasErrors, formErrors };
};

export const onCCFormChange = (
  name: string,
  value: string,
  state: IPaymentState,
  formErrors: IPaymentErrors,
  formatMessage: IntlFormatters['formatMessage'],
  supportedCardTypes: CardType[]
): { state: IPaymentState; formErrors: IPaymentErrors } => {
  if (name === FormFields.CARD_NUMBER) {
    const sanitizedNumber = sanitizeNumber(value);

    const { cardType, isValid } = validateCreditCardNumber(sanitizedNumber, supportedCardTypes);

    formErrors.cardType = '';
    if (cardType && !isValid) {
      formErrors.cardType = formatMessage({ id: 'unsupportedCardType' }, { cardType });
    }

    state.cardNumber = sanitizedNumber;
    state.cardType = cardType ?? '';
    state.isCardNumberValid = isValid;
  } else if (name === FormFields.EXPIRY) {
    formErrors.expiry = '';
    state.expiry = value;
    state.isExpiryValid = validateExpiry(value);
  } else if (name === FormFields.BILLING_ZIP) {
    formErrors.billingZip = '';
    state.billingZip = value;
  } else {
    if (typeof state[name] === 'boolean') {
      state[name] = !state[name];
    } else {
      formErrors[name] = '';
      state[name] = value;
    }
  }
  return { state, formErrors };
};

export const getCCFormErrors = (
  state: IPaymentState | IAdyenPaymentState,
  formErrors: IPaymentErrors,
  formatMessage: IntlFormatters['formatMessage'],
  shouldHideField: boolean,
  // Skips checks for Adyen cc fields as Adyen handles field validation
  skipCCFields?: boolean
): { hasErrors: boolean; formErrors: IPaymentErrors } => {
  if (!skipCCFields) {
    if (!state.isCardNumberValid) {
      formErrors.cardNumber = formatMessage({ id: 'ccNumberIsNotValid' });
    }
    if (state.cvv.trim().length < 3) {
      formErrors.cvv = formatMessage({ id: 'cvvMustBeAtLeast3Digits' });
    }
    if (!state.expiry.trim()) {
      formErrors.expiry = formatMessage({ id: 'expirationDateIsRequired' });
    }
    if (!state.isExpiryValid) {
      formErrors.expiry = formatMessage({ id: 'creditCardExpired' });
    }
  }
  if (!state.nameOnCard.trim()) {
    formErrors.nameOnCard = formatMessage({ id: 'nameOnCardIsRequired' });
  }
  if (!state.cardNumber.trim()) {
    formErrors.cardNumber = formatMessage({ id: 'cardNumberIsRequired' });
  }
  if (!state.cvv.trim()) {
    formErrors.cvv = formatMessage({ id: 'cvvIsRequired' });
  }
  if (!state.billingStreetAddress.trim()) {
    formErrors.billingStreetAddress = formatMessage({ id: 'addressRequiredError' });
  }
  if (!state.billingCity.trim()) {
    formErrors.billingCity = formatMessage({ id: 'cityRequiredError' });
  }
  if (!shouldHideField && !state.billingState.trim()) {
    formErrors.billingState = formatMessage({ id: 'stateIsARequiredField' });
  }
  if (!state.billingZip.trim()) {
    formErrors.billingZip = formatMessage({ id: 'zipCodeRequiredError' });
  }
  if (!state.billingCountry) {
    formErrors.billingCountry = getBillingCountryError(formatMessage);
  }
  const hasErrors: boolean = Object.values(formErrors).some(err => err);

  return { hasErrors, formErrors };
};

export const initialBillingAddress = (
  deliveryAddress: Partial<IDeliveryAddress> = {}
): ICCFormAddress => ({
  billingStreetAddress: delv(deliveryAddress, 'addressLine1', ''),
  billingApt: delv(deliveryAddress, 'addressLine2', ''),
  billingCity: delv(deliveryAddress, 'city', ''),
  billingState: delv(deliveryAddress, 'state', ''),
  billingZip: delv(deliveryAddress, 'zip', '')
});

export const initialPaymentState = ({
  isDelivery = false,
  deliveryAddress = {},
  billingCountry
}: {
  isDelivery?: boolean;
  billingCountry?: ISOs;
  deliveryAddress?: Partial<IDeliveryAddress>;
} = {}): IPaymentState | IAdyenPaymentState => ({
  nameOnCard: '',
  cardNumber: '',
  isCardNumberValid: false,
  isExpiryValid: false,
  cardType: '',
  cvv: '',
  expiry: '',
  ...initialBillingAddress(isDelivery ? deliveryAddress : {}),
  billingAddressSameAsDelivery: isDelivery,
  saveCard: true,
  billingCountry: billingCountry || DEFAULT_BILLING_COUNTRY
});

export const initialGiftCardPaymentState = (): IGiftCardPaymentState => ({
  cardNumber: '',
  isCardNumberValid: false,
  saveCard: true
});

export const testCardPaymentState = ({
  isDelivery = false
}: {
  isDelivery?: boolean;
} = {}): IPaymentState => ({
  nameOnCard: 'John Smith',
  cardNumber: '4111111111111111',
  isCardNumberValid: true,
  isExpiryValid: true,
  cardType: 'VISA',
  cvv: '123',
  expiry: '12/25',
  billingStreetAddress: '100 Universal City Plaza',
  billingApt: '',
  billingCity: 'Hollywood',
  billingState: 'CA',
  billingZip: '11747',
  billingCountry: ISOs.US,
  billingAddressSameAsDelivery: isDelivery,
  saveCard: true
});

export const initialErrorState = (): IPaymentErrors => ({
  nameOnCard: '',
  cardNumber: '',
  cardType: '',
  cvv: '',
  expiry: '',
  billingStreetAddress: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: '',
  billingApt: ''
});

export const getFraudPreventionValues = ({
  cardNumber,
  nameOnCard,
  billingStreetAddress,
  billingApt,
  billingCity,
  billingState,
  billingZip,
  billingCountry,
  expiry
}: IPaymentState) => {
  const ccBin: string = cardNumber.slice(0, 6);
  const ccLast4: string = cardNumber.slice(-4, cardNumber.length);

  return {
    billingAddress: {
      country: billingCountry,
      unitNumber: billingApt,
      locality: billingCity,
      postalCode: billingZip,
      region: billingState,
      streetAddress: billingStreetAddress
    },
    fullName: nameOnCard,
    ccBin,
    ccLast4,
    expiry
  };
};

export const mapDeliveryToCC = (deliveryAddress: IDeliveryAddress): ICCFormAddress => {
  return {
    billingStreetAddress: delv(deliveryAddress, 'addressLine1'),
    billingApt: delv(deliveryAddress, 'addressLine2'),
    billingCity: delv(deliveryAddress, 'city'),
    billingState: delv(deliveryAddress, 'state'),
    billingZip: delv(deliveryAddress, 'zip')
  };
};

export { mapCreditTypesToBackendTypes } from './map-credit-type-to-backend-type';

/**
 * This function check whether the selected payment method needs to be re-vaulted
 */
export const shouldReVault = ({
  accountIdentifier,
  methods
}: {
  accountIdentifier: string;
  methods: IPaymentMethod[];
}) => {
  // Get the clicked payment method object
  const clickedPaymentMethod = methods.find(method => {
    const selectedAccountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
    return selectedAccountIdentifier === accountIdentifier;
  });
  // Select the method if it's prepaid, apple pay, google pay or CC vaulted with Chase
  const isAppleOrGooglePay = isApplePay(accountIdentifier) || isGooglePay(accountIdentifier);
  return !(
    isAppleOrGooglePay ||
    clickedPaymentMethod?.prepaid ||
    clickedPaymentMethod?.chaseProfileId
  );
};

/**
 * This function check if the tip enter by the user is valid
 */
export const checkIfValidTip = ({
  tipAmount,
  subTotalCents
}: {
  tipAmount?: number;
  subTotalCents?: number;
}) => {
  const isTipLessThanSubTotal = Number(tipAmount) <= Number(subTotalCents);
  const isTipLessThanMax = Number(tipAmount) <= TipAmounts.MAX_AMOUNT;
  return !!(isTipLessThanSubTotal && isTipLessThanMax);
};
